import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { hasRole } from "../../auth/roles.js";
import {
    createRekoKnowledgeEntry,
    fetchRekoKnowledgeCategories,
    fetchRekoKnowledgeEntries,
} from "../../lib/rekoKnowledgeApi.js";

const EMPTY_FORM = {
    category_id: "",
    title: "",
    decision_type: "nfa",
    penalty_seconds: "",
    penalty_points: "",
    youtube_url: "",
    youtube_start_seconds: "0",
    short_description: "",
    decision_reasoning: "",
    tagsText: "",
};

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function decisionLabel(type) {
    const key = String(type || "").toLowerCase();

    if (key === "nfa") return "NFA";
    if (key === "penalty") return "Strafe";
    if (key === "warning") return "Verwarnung";
    if (key === "other") return "Sonstiges";

    return type || "Unbekannt";
}

function decisionPillClass(type) {
    const key = String(type || "").toLowerCase();

    if (key === "penalty") return "pill pill--pro";
    if (key === "nfa") return "pill pill--am";
    if (key === "warning") return "pill pill--int";

    return "pill pill--gesamt";
}

function parseTags(value) {
    return String(value || "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
}

function entryPenaltyText(entry) {
    const parts = [];

    if (entry?.penalty_seconds !== null && entry?.penalty_seconds !== undefined) {
        parts.push(`+${entry.penalty_seconds}s`);
    }

    if (entry?.penalty_points) {
        parts.push(`${entry.penalty_points} Strafpunkt${Number(entry.penalty_points) === 1 ? "" : "e"}`);
    }

    return parts.join(" · ");
}

function KnowledgeCreateForm({
                                 categories,
                                 selectedCategory,
                                 onCreated,
                             }) {
    const [form, setForm] = useState({
        ...EMPTY_FORM,
        category_id: selectedCategory?.id ? String(selectedCategory.id) : "",
    });

    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState("");
    const [localSuccess, setLocalSuccess] = useState("");

    useEffect(() => {
        if (!open) return;

        setForm((prev) => ({
            ...prev,
            category_id: selectedCategory?.id ? String(selectedCategory.id) : prev.category_id,
        }));
    }, [selectedCategory?.id, open]);

    function updateField(name, value) {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function resetForm() {
        setForm({
            ...EMPTY_FORM,
            category_id: selectedCategory?.id ? String(selectedCategory.id) : "",
        });
        setLocalError("");
        setLocalSuccess("");
    }

    async function submitEntry(e) {
        e.preventDefault();

        setLocalError("");
        setLocalSuccess("");

        if (!form.category_id) {
            setLocalError("Bitte eine Kategorie auswählen.");
            return;
        }

        if (!form.title.trim()) {
            setLocalError("Bitte einen Titel eintragen.");
            return;
        }

        if (!form.decision_reasoning.trim()) {
            setLocalError("Bitte die Entscheidungsbegründung eintragen.");
            return;
        }

        const payload = {
            category_id: Number(form.category_id),
            series_key: "gt3x",
            title: form.title.trim(),
            decision_type: form.decision_type,
            penalty_seconds:
                form.decision_type === "penalty" && form.penalty_seconds !== ""
                    ? Number(form.penalty_seconds)
                    : null,
            penalty_points:
                form.decision_type === "penalty" && form.penalty_points !== ""
                    ? Number(form.penalty_points)
                    : null,
            youtube_url: form.youtube_url.trim() || null,
            youtube_start_seconds: Number(form.youtube_start_seconds || 0),
            short_description: form.short_description.trim() || null,
            decision_reasoning: form.decision_reasoning.trim(),
            tags: parseTags(form.tagsText),
            is_active: true,
        };

        setBusy(true);

        try {
            await createRekoKnowledgeEntry(payload);
            setLocalSuccess("Fall wurde zur Wissensdatenbank hinzugefügt.");
            resetForm();
            setOpen(false);
            await onCreated?.();
        } catch (err) {
            setLocalError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <section className="card reko-knowledge-create-card">
            <div className="reko-knowledge-create-head">
                <div>
                    <h2>Neuen Fall hinzufügen</h2>
                    <p className="muted">
                        Nur ReKo Lead und Admin können neue Wissensdatenbank-Fälle anlegen.
                    </p>
                </div>

                <button
                    type="button"
                    className="btn btn--accent"
                    onClick={() => {
                        setOpen((prev) => !prev);
                        setLocalError("");
                        setLocalSuccess("");
                    }}
                >
                    {open ? "Formular schließen" : "Fall hinzufügen"}
                </button>
            </div>

            {localError && (
                <div className="rv-message rv-message--error" style={{ marginTop: 12 }}>
                    {localError}
                </div>
            )}

            {localSuccess && (
                <div className="rv-message rv-message--ok" style={{ marginTop: 12 }}>
                    {localSuccess}
                </div>
            )}

            {open && (
                <form className="reko-knowledge-create-form" onSubmit={submitEntry}>
                    <div className="reko-knowledge-form-grid reko-knowledge-form-grid--main">
                        <div>
                            <label>Kategorie</label>
                            <select
                                className="input rv-select"
                                value={form.category_id}
                                onChange={(e) => updateField("category_id", e.target.value)}
                                disabled={busy}
                            >
                                <option value="">Kategorie auswählen</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label>Titel</label>
                            <input
                                className="input"
                                value={form.title}
                                onChange={(e) => updateField("title", e.target.value)}
                                placeholder="z. B. Später Angriff innen am Apex"
                                disabled={busy}
                            />
                        </div>

                        <div>
                            <label>Entscheidung</label>
                            <select
                                className="input rv-select"
                                value={form.decision_type}
                                onChange={(e) => updateField("decision_type", e.target.value)}
                                disabled={busy}
                            >
                                <option value="nfa">NFA</option>
                                <option value="penalty">Strafe</option>
                                <option value="warning">Verwarnung</option>
                                <option value="other">Sonstiges</option>
                            </select>
                        </div>
                    </div>

                    {form.decision_type === "penalty" && (
                        <div className="reko-knowledge-form-grid">
                            <div>
                                <label>Strafsekunden</label>
                                <select
                                    className="input rv-select"
                                    value={form.penalty_seconds}
                                    onChange={(e) => updateField("penalty_seconds", e.target.value)}
                                    disabled={busy}
                                >
                                    <option value="">Keine Angabe</option>
                                    {[0, 5, 10, 15, 20, 25, 30].map((value) => (
                                        <option key={value} value={value}>
                                            {value} Sekunden
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label>Strafpunkte</label>
                                <select
                                    className="input rv-select"
                                    value={form.penalty_points}
                                    onChange={(e) => updateField("penalty_points", e.target.value)}
                                    disabled={busy}
                                >
                                    <option value="">Keine Angabe</option>
                                    {[1, 2, 3].map((value) => (
                                        <option key={value} value={value}>
                                            {value} Punkt{value > 1 ? "e" : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="reko-knowledge-form-grid reko-knowledge-form-grid--video">
                        <div>
                            <label>YouTube Link</label>
                            <input
                                className="input"
                                value={form.youtube_url}
                                onChange={(e) => updateField("youtube_url", e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=..."
                                disabled={busy}
                            />
                        </div>

                        <div>
                            <label>Start bei Sekunde</label>
                            <input
                                className="input"
                                type="number"
                                min="0"
                                value={form.youtube_start_seconds}
                                onChange={(e) => updateField("youtube_start_seconds", e.target.value)}
                                disabled={busy}
                            />
                        </div>
                    </div>

                    <div>
                        <label>Kurzbeschreibung des Vorfalls</label>
                        <textarea
                            className="input"
                            rows={3}
                            value={form.short_description}
                            onChange={(e) => updateField("short_description", e.target.value)}
                            placeholder="Was ist passiert? Kurz und sachlich beschreiben."
                            disabled={busy}
                        />
                    </div>

                    <div>
                        <label>Warum wurde so entschieden?</label>
                        <textarea
                            className="input"
                            rows={5}
                            value={form.decision_reasoning}
                            onChange={(e) => updateField("decision_reasoning", e.target.value)}
                            placeholder="Begründung für NFA oder Strafe. Möglichst so formulieren, dass zukünftige ReKo-Mitglieder daraus lernen können."
                            disabled={busy}
                        />
                    </div>

                    <div>
                        <label>Tags</label>
                        <input
                            className="input"
                            value={form.tagsText}
                            onChange={(e) => updateField("tagsText", e.target.value)}
                            placeholder="Überholen, Divebomb, Kontakt, Apex"
                            disabled={busy}
                        />
                    </div>

                    <div className="reko-knowledge-create-actions">
                        <button
                            type="button"
                            className="btn"
                            onClick={resetForm}
                            disabled={busy}
                        >
                            Zurücksetzen
                        </button>

                        <button
                            type="submit"
                            className="btn btn--accent"
                            disabled={busy}
                        >
                            {busy ? "Speichere ..." : "Fall speichern"}
                        </button>
                    </div>
                </form>
            )}
        </section>
    );
}

function CategoryOverview({
                              categories,
                              onSelectCategory,
                          }) {
    return (
        <section className="reko-knowledge-category-grid">
            {categories.map((category) => (
                <button
                    key={category.id}
                    type="button"
                    className="reko-knowledge-category-card"
                    style={{ "--cat-accent": category.accent_color || "var(--accent)" }}
                    onClick={() => onSelectCategory(category)}
                >
                    <div className="reko-knowledge-category-card__icon">
                        {category.icon_emoji || "📚"}
                    </div>

                    <div>
                        <h2>{category.title}</h2>

                        {category.description && (
                            <p>{category.description}</p>
                        )}
                    </div>

                    <div className="reko-knowledge-category-card__footer">
                        <span className="pill pill--gesamt">
                            {category.entry_count} Fall{Number(category.entry_count) === 1 ? "" : "e"}
                        </span>

                        <span className="reko-knowledge-card-arrow">
                            →
                        </span>
                    </div>
                </button>
            ))}
        </section>
    );
}

function EntryDetail({
                         selectedCategory,
                         entries,
                         selectedEntry,
                         selectedEntryId,
                         setSelectedEntryId,
                         q,
                         setQ,
                         decisionType,
                         setDecisionType,
                         onSearch,
                         onBack,
                     }) {
    return (
        <>
            <div className="card reko-knowledge-category-head">
                <button type="button" className="btn" onClick={onBack}>
                    ← Zurück zu Bereichen
                </button>

                <div className="reko-knowledge-category-title">
                    <span className="reko-knowledge-category-title__icon">
                        {selectedCategory?.icon_emoji || "📚"}
                    </span>

                    <div>
                        <h2>{selectedCategory?.title || "Bereich"}</h2>

                        {selectedCategory?.description && (
                            <p className="muted">{selectedCategory.description}</p>
                        )}
                    </div>
                </div>
            </div>

            <form className="card reko-knowledge-filters" onSubmit={onSearch}>
                <div>
                    <label>Suche innerhalb des Bereichs</label>
                    <input
                        className="input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="z. B. Apex, innen, Kontakt, Vorteil ..."
                    />
                </div>

                <div>
                    <label>Entscheidung</label>
                    <select
                        className="input rv-select"
                        value={decisionType}
                        onChange={(e) => setDecisionType(e.target.value)}
                    >
                        <option value="">Alle Entscheidungen</option>
                        <option value="nfa">NFA</option>
                        <option value="penalty">Strafe</option>
                        <option value="warning">Verwarnung</option>
                        <option value="other">Sonstiges</option>
                    </select>
                </div>

                <div className="reko-knowledge-filter-actions">
                    <button type="submit" className="btn btn--accent">
                        Suchen
                    </button>
                </div>
            </form>

            {!entries.length && (
                <div className="card">
                    <h2>Noch keine Fälle</h2>
                    <p className="muted">
                        In diesem Bereich sind noch keine Wissensdatenbank-Einträge vorhanden.
                    </p>
                </div>
            )}

            {entries.length > 0 && (
                <section className="reko-knowledge-layout">
                    <aside className="card reko-knowledge-list">
                        <h2>Fälle</h2>

                        <div className="reko-knowledge-list-items">
                            {entries.map((entry) => (
                                <button
                                    key={entry.id}
                                    type="button"
                                    className={
                                        String(selectedEntryId) === String(entry.id)
                                            ? "reko-knowledge-list-item is-active"
                                            : "reko-knowledge-list-item"
                                    }
                                    onClick={() => setSelectedEntryId(String(entry.id))}
                                >
                                    <span className={decisionPillClass(entry.decision_type)}>
                                        {decisionLabel(entry.decision_type)}
                                    </span>

                                    <strong>{entry.title}</strong>

                                    {entryPenaltyText(entry) && (
                                        <small>{entryPenaltyText(entry)}</small>
                                    )}
                                </button>
                            ))}
                        </div>
                    </aside>

                    <article className="card reko-knowledge-detail">
                        {selectedEntry && (
                            <>
                                <div className="reko-knowledge-detail-head">
                                    <div>
                                        <span className={decisionPillClass(selectedEntry.decision_type)}>
                                            {decisionLabel(selectedEntry.decision_type)}
                                        </span>

                                        <h2>{selectedEntry.title}</h2>

                                        {selectedEntry.category_title && (
                                            <p>{selectedEntry.category_title}</p>
                                        )}
                                    </div>

                                    {entryPenaltyText(selectedEntry) && (
                                        <div className="reko-knowledge-penalty-box">
                                            <strong>{entryPenaltyText(selectedEntry)}</strong>
                                        </div>
                                    )}
                                </div>

                                {selectedEntry.video_embed_url ? (
                                    <div className="reko-knowledge-video">
                                        <iframe
                                            src={selectedEntry.video_embed_url}
                                            title={selectedEntry.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen
                                        />
                                    </div>
                                ) : (
                                    <div className="rv-message rv-message--error">
                                        Kein Video hinterlegt.
                                    </div>
                                )}

                                {selectedEntry.short_description && (
                                    <section className="reko-knowledge-section">
                                        <h3>Vorfall</h3>
                                        <p>{selectedEntry.short_description}</p>
                                    </section>
                                )}

                                <section className="reko-knowledge-section">
                                    <h3>Warum wurde so entschieden?</h3>
                                    <p style={{ whiteSpace: "pre-line" }}>
                                        {selectedEntry.decision_reasoning}
                                    </p>
                                </section>

                                {Array.isArray(selectedEntry.tags) && selectedEntry.tags.length > 0 && (
                                    <div className="reko-knowledge-tags">
                                        {selectedEntry.tags.map((tag) => (
                                            <span key={tag} className="pill pill--gesamt">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </article>
                </section>
            )}
        </>
    );
}

export default function RekoKnowledge() {
    const { user } = useAuth();

    const canEdit =
        hasRole(user, "admin") ||
        hasRole(user, "reko_lead") ||
        hasRole(user, "reko-lead");

    const [categories, setCategories] = useState([]);
    const [entries, setEntries] = useState([]);

    const [selectedCategorySlug, setSelectedCategorySlug] = useState("");
    const [selectedEntryId, setSelectedEntryId] = useState("");

    const [q, setQ] = useState("");
    const [decisionType, setDecisionType] = useState("");

    const [loading, setLoading] = useState(true);
    const [entryLoading, setEntryLoading] = useState(false);
    const [error, setError] = useState("");

    const selectedCategory = useMemo(() => {
        return categories.find((category) => category.slug === selectedCategorySlug) || null;
    }, [categories, selectedCategorySlug]);

    const selectedEntry = useMemo(() => {
        return entries.find((entry) => String(entry.id) === String(selectedEntryId)) || entries[0] || null;
    }, [entries, selectedEntryId]);

    async function loadCategories() {
        setError("");

        const data = await fetchRekoKnowledgeCategories({
            seriesKey: "gt3x",
        });

        setCategories(Array.isArray(data?.categories) ? data.categories : []);
    }

    async function loadEntries(nextCategorySlug = selectedCategorySlug) {
        if (!nextCategorySlug) {
            setEntries([]);
            setSelectedEntryId("");
            return;
        }

        setEntryLoading(true);
        setError("");

        try {
            const data = await fetchRekoKnowledgeEntries({
                categorySlug: nextCategorySlug,
                q,
                decisionType,
                seriesKey: "gt3x",
            });

            const rows = Array.isArray(data?.entries) ? data.entries : [];
            setEntries(rows);

            if (rows.length) {
                setSelectedEntryId((current) => {
                    const stillExists = rows.some((entry) => String(entry.id) === String(current));
                    return stillExists ? current : String(rows[0].id);
                });
            } else {
                setSelectedEntryId("");
            }
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setEntryLoading(false);
        }
    }

    async function reloadAll() {
        setLoading(true);

        try {
            await loadCategories();

            if (selectedCategorySlug) {
                await loadEntries(selectedCategorySlug);
            }
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reloadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function selectCategory(category) {
        setSelectedCategorySlug(category.slug);
        setQ("");
        setDecisionType("");
        setSelectedEntryId("");

        setEntryLoading(true);
        setError("");

        try {
            const data = await fetchRekoKnowledgeEntries({
                categorySlug: category.slug,
                seriesKey: "gt3x",
            });

            const rows = Array.isArray(data?.entries) ? data.entries : [];
            setEntries(rows);

            if (rows.length) {
                setSelectedEntryId(String(rows[0].id));
            }
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setEntryLoading(false);
        }
    }

    function backToCategories() {
        setSelectedCategorySlug("");
        setEntries([]);
        setSelectedEntryId("");
        setQ("");
        setDecisionType("");
        setError("");
    }

    function submitSearch(e) {
        e.preventDefault();
        loadEntries(selectedCategorySlug);
    }

    async function afterCreated() {
        await loadCategories();

        if (selectedCategorySlug) {
            await loadEntries(selectedCategorySlug);
        }
    }

    if (loading) {
        return (
            <div className="card reko-modern-page">
                <h1>ReKo Wissensdatenbank</h1>
                <p className="muted">Lade Daten ...</p>
            </div>
        );
    }

    return (
        <div className="reko-modern-page reko-knowledge-page">
            <section className="reko-page-hero">
                <div className="reko-page-hero__inner">
                    <div>
                        <div className="reko-page-hero__eyebrow">
                            RaceVision Stewarding
                        </div>

                        <h1>ReKo Wissensdatenbank</h1>

                        <p className="muted">
                            Interne Sammlung vergangener Vorfälle mit Video, Entscheidung und Begründung.
                            Wähle zuerst einen Bereich, danach findest du passende Beispiele und Auslegungen.
                        </p>
                    </div>

                    <div className="reko-page-hero__meta">
                        <span className="pill pill--gesamt">
                            {categories.length} Bereiche
                        </span>

                        {canEdit && (
                            <span className="pill pill--role-reko-lead">
                                Lead-Modus
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {error && (
                <div className="rv-message rv-message--error">
                    {error}
                </div>
            )}

            {canEdit && (
                <KnowledgeCreateForm
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onCreated={afterCreated}
                />
            )}

            {!selectedCategorySlug && (
                <CategoryOverview
                    categories={categories}
                    onSelectCategory={selectCategory}
                />
            )}

            {selectedCategorySlug && entryLoading && (
                <div className="card">
                    Lade Fälle ...
                </div>
            )}

            {selectedCategorySlug && !entryLoading && (
                <EntryDetail
                    selectedCategory={selectedCategory}
                    entries={entries}
                    selectedEntry={selectedEntry}
                    selectedEntryId={selectedEntryId}
                    setSelectedEntryId={setSelectedEntryId}
                    q={q}
                    setQ={setQ}
                    decisionType={decisionType}
                    setDecisionType={setDecisionType}
                    onSearch={submitSearch}
                    onBack={backToCategories}
                />
            )}
        </div>
    );
}